import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "emailSettingPreferences__skeletons"
}
const _hoisted_2 = { class: "emailSettingPreferences__container emailSettingPreferences__separator" }
const _hoisted_3 = { class: "emailSettingPreferences__content" }
const _hoisted_4 = {
  key: 0,
  class: "emailSettingPreferences__content"
}
const _hoisted_5 = { class: "emailSettingPreferences__container" }
const _hoisted_6 = { class: "emailSettingPreferences__content" }
const _hoisted_7 = { class: "emailSettingPreferences__settingTitle" }
const _hoisted_8 = { class: "emailSettingPreferences__settingDescription" }

import { ref, computed, inject, watch, onMounted } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import { useStore } from 'vuex';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import EmailSettingPreferencesEmailsInput from 'vcomponents/email/emailSettings/EmailSettingPreferencesEmailsInput.vue';
import StaffApi from 'api/staff/staff';
import { setEmailSettings } from 'api/emailEngine/emailEngineService';

interface Props {
    privateEmail: boolean;
    automaticallyAttachDocumentValue: boolean;
    ccEmails: string[];
    isLoading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailSettingPreferences',
  props: {
    privateEmail: { type: Boolean },
    automaticallyAttachDocumentValue: { type: Boolean },
    ccEmails: {},
    isLoading: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const errorsHandler = store.state.system.errorsHandler;
const staffId = store.getters['user/getUser'].id;

const privateEmailPermissions = ref(false);
const privateEmailPermissionsLoading = ref(true);
const localPrivateEmailValue = ref(props.privateEmail);
const disablePrivateEmailSwitch = ref(false);
const localAutomaticallyAttachDocumentValue = ref(props.automaticallyAttachDocumentValue);
const disableAutomaticallyAttachDocumentSwitch = ref(false);
const ccEmails = ref<string[]>(props.ccEmails || []);

const privateEmailSwitchToasterLabel = computed(() => {
    return localPrivateEmailValue.value
        ? $t.__('email.EmailSettingPreferences.privateEmails.isPrivate')
        : $t.__('email.EmailSettingPreferences.privateEmails.isNotPrivate');
});

const getPrivateEmailPermission = () => {
    StaffApi.getStaffPermission(staffId)
        .then((response) => {
            const { _embed } = response.data as { _embed: { acl: Record<string, boolean> } };
            privateEmailPermissions.value = _embed?.acl?.update_email_privacy ?? false;
            privateEmailPermissionsLoading.value = false;
        })
        .catch((error: Error) => {
            errorsHandler.logException(error);
            privateEmailPermissionsLoading.value = false;
        });
};

const handlePrivateEmailSwitch = () => {
    if (disablePrivateEmailSwitch.value) {
        return;
    }
    disablePrivateEmailSwitch.value = true;
    localPrivateEmailValue.value = !localPrivateEmailValue.value;
    setEmailSettings({ private_mode: localPrivateEmailValue.value })
        .then(() => {
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: privateEmailSwitchToasterLabel.value,
            });
        })
        .catch((error: Error) => {
            localPrivateEmailValue.value = !localPrivateEmailValue.value;
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.privateEmails.error'),
            });
        })
        .finally(() => {
            disablePrivateEmailSwitch.value = false;
        });
};

const handleAutomaticallyAttachDocumentSwitch = () => {
    if (disableAutomaticallyAttachDocumentSwitch.value) {
        return;
    }
    disableAutomaticallyAttachDocumentSwitch.value = true;
    localAutomaticallyAttachDocumentValue.value = !localAutomaticallyAttachDocumentValue.value;
    setEmailSettings({ automatically_attach_document: localAutomaticallyAttachDocumentValue.value })
        .then(() => {
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.automaticallyAttachDocument.success'),
            });
        })
        .catch((error: Error) => {
            localAutomaticallyAttachDocumentValue.value =
                !localAutomaticallyAttachDocumentValue.value;
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.automaticallyAttachDocument.error'),
            });
        })
        .finally(() => {
            disableAutomaticallyAttachDocumentSwitch.value = false;
        });
};

const handleCcEmailsUpdate = (emails: string[]) => {
    setEmailSettings({ cc_emails: emails })
        .then(() => {
            ccEmails.value = emails;
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.emailsAddedToCC.success'),
            });
        })
        .catch((error: Error) => {
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.emailsAddedToCC.error'),
            });
        });
};

watch(
    () => [props.privateEmail, props.automaticallyAttachDocumentValue, props.ccEmails] as const,
    ([newPrivateEmail, newAttachDocument, newCcEmails]) => {
        localPrivateEmailValue.value = newPrivateEmail;
        localAutomaticallyAttachDocumentValue.value = newAttachDocument;
        ccEmails.value = newCcEmails || [];
    },
);

onMounted(() => {
    getPrivateEmailPermission();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassSwitch = _resolveComponent("CompassSwitch")!

  return (_openBlock(), _createBlock(Card, null, {
    default: _withCtx(() => [
      _createVNode(Heading, {
        labelTitle: _unref($t).__('email.EmailSettingPreferences.title'),
        variant: "h2",
        prefixColoredIcon: { icon: 'tool', color: 'brand' }
      }, null, 8 /* PROPS */, ["labelTitle"]),
      (__props.isLoading || privateEmailPermissionsLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_CompassSkeleton, {
              class: "emailSettingPreferences__skeleton--large",
              size: _unref(CompassEnums).SkeletonSize.EXTRA_LARGE
            }, null, 8 /* PROPS */, ["size"]),
            _createVNode(_component_CompassSkeleton, {
              class: "emailSettingPreferences__skeleton--large",
              size: _unref(CompassEnums).SkeletonSize.EXTRA_LARGE
            }, null, 8 /* PROPS */, ["size"]),
            _createVNode(_component_CompassSkeleton, {
              class: "emailSettingPreferences__skeleton--medium",
              size: _unref(CompassEnums).SkeletonSize.EXTRA_LARGE
            }, null, 8 /* PROPS */, ["size"]),
            _createVNode(_component_CompassSkeleton, {
              class: "emailSettingPreferences__skeleton--small",
              size: _unref(CompassEnums).SkeletonSize.LARGE
            }, null, 8 /* PROPS */, ["size"])
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_CompassSwitch, {
                  modelValue: localAutomaticallyAttachDocumentValue.value,
                  disabled: disableAutomaticallyAttachDocumentSwitch.value,
                  label: 
                            _unref($t).__('email.EmailSettingPreferences.automaticallyAttachDocument.title')
                        ,
                  caption: 
                            _unref($t).__(
                                'email.EmailSettingPreferences.automaticallyAttachDocument.description',
                            )
                        ,
                  orientation: _unref(CompassEnums).SwitchOrientation.LEFT,
                  onClick: handleAutomaticallyAttachDocumentSwitch
                }, null, 8 /* PROPS */, ["modelValue", "disabled", "label", "caption", "orientation"])
              ]),
              (privateEmailPermissions.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_component_CompassSwitch, {
                      modelValue: localPrivateEmailValue.value,
                      disabled: disablePrivateEmailSwitch.value,
                      label: _unref($t).__('email.EmailSettingPreferences.privateEmails.title'),
                      caption: _unref($t).__('email.EmailSettingPreferences.privateEmails.description'),
                      orientation: _unref(CompassEnums).SwitchOrientation.LEFT,
                      onClick: handlePrivateEmailSwitch
                    }, null, 8 /* PROPS */, ["modelValue", "disabled", "label", "caption", "orientation"])
                  ]))
                : _createCommentVNode("v-if", true)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("p", _hoisted_7, _toDisplayString(_unref($t).__('email.EmailSettingPreferences.emailsAddedToCC.title')), 1 /* TEXT */),
                _createElementVNode("p", _hoisted_8, _toDisplayString(_unref($t).__('email.EmailSettingPreferences.emailsAddedToCC.description')), 1 /* TEXT */),
                _createVNode(EmailSettingPreferencesEmailsInput, {
                  modelValue: ccEmails.value,
                  "onUpdate:modelValue": handleCcEmailsUpdate
                }, null, 8 /* PROPS */, ["modelValue"])
              ])
            ])
          ], 64 /* STABLE_FRAGMENT */))
    ]),
    _: 1 /* STABLE */
  }))
}
}

})